/**
 * 最終形態之後 (D-253). Levels keep climbing past Lv.30 on the same curve (src/pet-modes.ts) up to Lv.90, and every 5 levels
 * after the final form a companion earns a mark: a star at Lv.35, 40, 45; four stars become a moon at Lv.50 (owner: QQ 寵物
 * style, stars → moon → sun); a second moon at Lv.70; at Lv.90 everything becomes one sun, the top. So `steps` runs 0..12 and is
 * drawn as at most 2 moons + 3 stars, or a sun alone.
 * Marks are never stored: like forms they follow the level, so an undo or a 提醒扣點 takes one back the same way it takes a form
 * back. They show only on a pet DRAWN at its final form, so with manual evolution (D-214) the tap to form 4 comes first.
 * Pure functions, no D1 or DOM: the Worker, the web app and the tour all import this file.
 */
export const FIRST_MARK_LEVEL = 35;
export const MARK_EVERY = 5;
/** The highest level a companion reaches (src/pet-modes.ts stops the curve here); it is also the sun. */
export const PET_MAX_LEVEL = 90;
const STARS_PER_MOON = 4;
export const MAX_MARK_STEPS = (PET_MAX_LEVEL - 30) / MARK_EVERY;

export type MarkKind = "star" | "moon" | "sun";
export type Marks = { suns: number; moons: number; stars: number; steps: number; /** The biggest mark held, null below the first. */ tier: MarkKind | null };

/** Marks a level has earned (steps 0 below Lv.35). */
export const marksForLevel = (level: number): number => level >= FIRST_MARK_LEVEL ? Math.floor((Math.min(level, PET_MAX_LEVEL) - 30) / MARK_EVERY) : 0;

/** Marks a pet shows: only once its final form is the one drawn (visualStage 30). */
export const shownMarks = (level: number, visualStage: number): number => visualStage >= 30 ? marksForLevel(level) : 0;

/** How `steps` is drawn: a sun alone at the top, else up to 2 moons and 3 stars. */
export function marksOf(steps: number): Marks {
  const n = Math.max(0, Math.min(MAX_MARK_STEPS, Math.floor(steps)));
  if (n >= MAX_MARK_STEPS) return { suns: 1, moons: 0, stars: 0, steps: n, tier: "sun" };
  const moons = Math.floor(n / STARS_PER_MOON), stars = n % STARS_PER_MOON;
  return { suns: 0, moons, stars, steps: n, tier: moons ? "moon" : stars ? "star" : null };
}

/** The mark that reaching `steps` gives: a star, the stars turning into a moon (every 4th), or the sun (the last). */
export const markKindAt = (steps: number): MarkKind => steps >= MAX_MARK_STEPS ? "sun" : steps % STARS_PER_MOON === 0 ? "moon" : "star";

/** The biggest mark crossed going from `from` to `to` steps (a jump over a moon announces the moon, not the star after it). */
export const crossedKind = (from: number, to: number): MarkKind =>
  to >= MAX_MARK_STEPS && from < MAX_MARK_STEPS ? "sun" : Math.floor(to / STARS_PER_MOON) > Math.floor(Math.max(0, from) / STARS_PER_MOON) ? "moon" : "star";

/** The level of the next mark, or null before the final form level and after the sun. */
export const nextMarkLevel = (level: number): number | null => {
  if (level < 30) return null;
  const next = 30 + (marksForLevel(level) + 1) * MARK_EVERY;
  return next > PET_MAX_LEVEL ? null : next;
};

/** Plain-text marks ("☾★★"), for places that draw text only: the pet world's name tag, the growth card. U+FE0E keeps them text. */
export function markText(steps: number): string {
  const marks = marksOf(steps);
  return "☀︎".repeat(marks.suns) + "☾︎".repeat(marks.moons) + "★".repeat(marks.stars);
}
